import { SecurityUtils } from "./SecurityUtils";

/**
 * Who may accept a Data Validation discrepancy (REV-17124).
 *
 * Accepting one is an accounting judgement about a number that will appear in
 * a merchant's financial statements, so on the merchant side it sits with the
 * roles that carry that responsibility: Admin and Controller. Chargebee staff
 * can act on a merchant's behalf, but only from level 4 up.
 *
 * This lives in `revlock-webutils` rather than next to the recon code in
 * `revlock-core` because `app/actg` cannot import core, and the button the UI
 * renders has to agree with the check the API enforces. One predicate, two
 * callers.
 */

/** Lowest internal level that may accept a discrepancy. */
export const RECON_IGNORE_MIN_SUPER_ADMIN_LEVEL =
    SecurityUtils.SUPER_ADMIN_LEVEL_4;

export type ReconIgnoreActor = {
    /**
     * The acting Chargebee user's level, or 0 for a merchant user.
     *
     * Must be the *real* actor's level, not the impersonated user's. On the API
     * that is `req.superAdminLevel`, captured before impersonation swaps
     * `req.user`; in the UI it is `user.actualSuperAdminLevel`.
     */
    superAdminLevel?: number;
    /** Whether the tenant user being acted as holds Admin or Controller. */
    hasTenantIgnoreRole?: boolean;
};

/**
 * Note the order: an internal actor is judged on level alone and never falls
 * through to the tenant role. `SecurityUtils.hasRole` treats any level >= 1 as
 * holding every role, so asking it first would let level 1 through and there
 * would be no level floor at all.
 */
export const canIgnoreReconErrors = ({
    superAdminLevel = 0,
    hasTenantIgnoreRole = false
}: ReconIgnoreActor = {}): boolean => {
    if (superAdminLevel > 0) {
        return superAdminLevel >= RECON_IGNORE_MIN_SUPER_ADMIN_LEVEL;
    }

    return hasTenantIgnoreRole;
};
